
import React from "react";
import styles from "../assets/style.module.css";
import { ApiResponseData, BaseService, IProfileWebsite, ITournamentEditionMatch, OmneHostService, SanitizedResponse } from "../../../../services";
import { BoolHelper, Classname, Paginator, Scheme, useSearchParams } from "@rivtn/noseur";
import { Utils } from "../../../../utils";

export function buildMatchView(publicId: string, website: IProfileWebsite, t: ITournamentEditionMatch) {
    const ownScores = Object.values(Object.values(t.scores)?.[0] ?? {}).map((s) => Object.values(s)?.[0]);
    const won = (ownScores?.[0] ?? 0) > ownScores?.[1];
    const [first, ...others] = t.tournament_edition_registered_participant_ids?.map((external_id) => ({ ...t.extra_data?.participants_profiles?.[external_id], external_id }));
    const flattenedOwnMembers = JSON.stringify(t.extra_data?.participants_profiles?.[first.external_id + "_members"]);
    const isHome = (first.external_id === website.member.external_id || first.username === website.member.username)
        || (flattenedOwnMembers?.includes(website.member.external_id) || flattenedOwnMembers?.includes(website.member.username));

    return (<a key={t.external_id} className={Classname.build(styles["match-row"], styles["unlinked"])} target="_blank" href={Utils.buildTournamentMatchLink(publicId, t)}>
        <div className={`${styles["match-status-dot"]} ${t.status === "ONGOING" ? styles["dot-ongoing"] :
            t.status === "UPCOMING" ? styles["dot-upcoming"] :
                won ? styles["dot-past-win"] : styles["dot-past-loss"]
            }`} />
        <div className={styles["match-date"]}>{Utils.fineDate(new Date(t.start_date))}</div>
        <div className={styles["match-opp"]}>
            <div>
                <img alt={first.external_id} src={first.profile_picture_url ?? first.logo_url} />
                <span className="name">{first.name ?? first.username}</span>
            </div>
            {(others?.length > 0) && (<span className={styles["vs"]}>vs</span>)}
            {others.reduce((acc: any[], other: any, index: number) => {
                acc.push(<div key={"" + index + other.external_id}>
                    <img alt={other.external_id} src={other.profile_picture_url ?? other.logo_url} />
                    <span className="name">{other.name ?? other.username}</span>
                </div>);
                if (index < (others.length - 1)) acc.push(<span key={"sep" + index + other.external_id} style={{ marginRight: 10 }}>,</span>);
                return acc;
            }, [])}
        </div>

        <div className={styles["match-comp"]}>{t.tournament_edition.name}</div>
        <div className={styles["match-hoa"]}>{isHome ? "Home" : "Away"}</div>
        <div className={styles["match-score"]} style={{
            color: t.status === "ONGOING" ? "var(--green)" :
                t.status === "ENDED" && won ? "var(--green)" :
                    t.status === "ENDED" && !won ? "var(--red)" : "var(--muted)"
        }}>
            {ownScores ? `${ownScores?.[0] ?? ""} - ${ownScores?.slice(1)?.join(", ") ?? ""}` : (t.status === "UPCOMING" ? "TBD" : "-")}
        </div>
    </a>);
}

export interface ScheduleProps {
    publicId: string;
    website: IProfileWebsite;
}

function Schedule(props: ScheduleProps) {

    const { website, publicId } = props;
    const omnehostService = OmneHostService.getInstance();
    const sections = [["UPCOMING", "Upcoming"], ["ONGOING", "Live"], ["ENDED", "Past"]];
    const [matches, setMatches] = React.useState<ApiResponseData<ITournamentEditionMatch>>();
    const { page, setPage, status, setStatus, tipe, setTipe } = useSearchParams({ defaultInit: window.location.search, params: { page: 1, status: "", tipe: "" } });

    React.useEffect(fetchInformation, [omnehostService, page, status, tipe]);

    return (<div className={styles["section"]}>
        <div className={styles["sec-line"]} />
        <h1 className={styles["sec-title"]}>Match <em>Schedule</em></h1>
        <p className={styles["sec-sub"]}>All fixtures — past, present and upcoming</p>
        <div className={styles["tabs"]}>
            {[["", "Personal & Teams"], ['"MEMBER"', "Personal"], ['"TEAM"', "Teams"]].filter((_, i) => {
                if ((BoolHelper.equalsAny(i, [0, 2])) && !website.schedule_settings.show_past_team_matches && !website.schedule_settings.show_ongoing_upcoming_team_matches) {
                    return false;
                } else if ((BoolHelper.equalsAny(i, [0, 1])) && !website.schedule_settings.show_past_personal_matches && !website.schedule_settings.show_ongoing_upcoming_personal_matches) {
                    return false;
                }
                return true;
            }).map(([v, l]) => (
                <button key={v} className={`${styles["tab-btn"]}${tipe === v ? ` ${styles["active"]}` : ""}`} onClick={() => setTipe(v)}>{l}</button>
            ))}
        </div>
        <div className={styles["tabs"]}>
            {[["", "All Matches"], ...sections].filter(([status, _]) => {
                if (status === "" && (!website.schedule_settings.show_past_team_matches || !website.schedule_settings.show_past_personal_matches
                    || !website.schedule_settings.show_ongoing_upcoming_team_matches || !website.schedule_settings.show_ongoing_upcoming_personal_matches)) {
                    return false;
                } else if (status === "ENDED" && ((tipe === '"TEAM"' && !website.schedule_settings.show_past_team_matches) || (tipe === '"MEMBER"' && !website.schedule_settings.show_past_personal_matches))) {
                    return false;
                } else if (BoolHelper.equalsAny(status, ["UPCOMING", "ONGOING"]) && ((tipe === '"TEAM"' && !website.schedule_settings.show_ongoing_upcoming_team_matches) || (tipe === '"MEMBER"' && !website.schedule_settings.show_ongoing_upcoming_personal_matches))) {
                    return false;
                }
                return true;
            }).map(([v, l]) => (
                <button key={v} className={`${styles["tab-btn"]}${status === v ? ` ${styles["active"]}` : ""}`} onClick={() => setStatus(v)}>{l}</button>
            ))}
        </div>

        {(!!matches?.content)
            ? ((matches.content && matches.content?.length > 0)
                ? (<div className={styles["matches-brief"]}>
                    {matches.content!.map((t) => buildMatchView(publicId, website, t))}
                </div>)
                : (Utils.emptyState()))
            : (Utils.loadingState())}

        <Paginator scheme={Scheme.LIGHT} style={{ marginTop: 50 }} rowsPerPage={matches?.size} totalRecords={matches?.total_elements} initialPage={parseInt("" + page)} onPageChange={(e) => {
            setPage(e.currentPage)
        }} template={{
            activePageLabel: () => "",
            nextPageElement: (option) => (<button className={Classname.build(`${styles["page-btn"]}`, option.className, "fa fa-angle-right")} style={{ margin: "0px 2.5px" }} disabled={option.disabled} onClick={option.onClick} />),
            previousPageElement: (option) => (<button className={Classname.build(`${styles["page-btn"]}`, option.className, "fa fa-angle-left")} style={{ margin: "0px 2.5px" }} disabled={option.disabled} onClick={option.onClick} />),
            lastPageElement: (option) => (<button className={Classname.build(`${styles["page-btn"]}`, option.className, "fa fa-angle-double-right")} style={{ margin: "0px 2.5px" }} disabled={option.disabled} onClick={option.onClick} />),
            firstPageElement: (option) => (<button className={Classname.build(`${styles["page-btn"]}`, option.className, "fa fa-angle-double-left")} style={{ margin: "0px 2.5px" }} disabled={option.disabled} onClick={option.onClick} />),
            pageElement: (option) => (<button className={Classname.build(`${styles["page-btn"]}`, option.className, (option.active ? styles["active"] : null))} style={{ margin: "0px 2.5px" }} disabled={option.disabled} onClick={option.onClick}>{option.label}</button>),
        }} />
    </div>);

    function fetchInformation() {
        setMatches({});
        omnehostService.queryMemberSchedule(website.member.username, {
            page,
            size: 20,
            status__eq: status,
            extra_data__like: tipe,
            sort: ["start_date,ASC", "status,ASC"],
        }).then(({ apiResponse }: SanitizedResponse<ITournamentEditionMatch>) => {
            setMatches(apiResponse.data!);
        }).catch(BaseService.reportError);
    }

}

export default Schedule;